// SupplyRatesPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { fetchSupplyRates, fetchRegions } from '../services/api';
import { useRegion } from '../context/RegionContext';
import './PageLayout.css';

const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

// 🆕 Helper function to determine date color status
const getDateStatus = (dateAdded) => {
  if (!dateAdded) return 'red'; // No date = red
  
  const now = new Date();
  const addedDate = new Date(dateAdded);
  const monthsDiff = (now - addedDate) / (1000 * 60 * 60 * 24 * 30); // Approximate months
  
  if (monthsDiff <= 2) return 'green';  // Within 2 months
  if (monthsDiff <= 4) return 'yellow'; // 2-4 months
  return 'red'; // Over 4 months (you mentioned 6, but based on your description: green=2, yellow=4, red=6+)
};

// 🆕 Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-NZ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

function SupplyRatesPage() {
  const { selectedRegion } = useRegion();

  const [materials, setMaterials] = useState([]);
  const [filters, setFilters] = useState({});
  const [categories, setCategories] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const regions = await fetchRegions();
        console.log('🔍 FRONTEND: Fetched regions:', regions);
        setAvailableRegions(regions || []);
      } catch (e) {
        console.error('Error loading regions:', e);
      }
    })();
  }, []);

  const regionName = useMemo(() => {
    const r = availableRegions.find((r) => r.id === selectedRegion);
    console.log('🔍 FRONTEND: Finding region name for selectedRegion:', selectedRegion);
    console.log('🔍 FRONTEND: Available regions:', availableRegions);
    console.log('🔍 FRONTEND: Found region object:', r);
    console.log('🔍 FRONTEND: Final regionName:', r ? r.name : selectedRegion);
    return r ? r.name : selectedRegion;
  }, [availableRegions, selectedRegion]);

  useEffect(() => {
    console.log('🔍 FRONTEND: useEffect triggered with selectedRegion:', selectedRegion);
    
    if (!selectedRegion) {
      console.log('🔍 FRONTEND: No selectedRegion, returning early');
      return;
    }

    console.log('🔍 FRONTEND: About to call fetchSupplyRates with regionId:', selectedRegion);
    
    fetchSupplyRates(selectedRegion)
      .then((res) => {
        console.log('🔍 FRONTEND: fetchSupplyRates response:', res);
        console.log('🔍 FRONTEND: res.data:', res?.data);
        
        const rows = toArray(res?.data);
        console.log('🔍 FRONTEND: Processed rows:', rows);
        console.log('🔍 FRONTEND: Number of rows:', rows.length);
        console.log('🔍 FRONTEND: Sample row with date:', rows[0]); // 🆕 Check date data
        
        setMaterials(rows);
        
        const uniqueCategories = [...new Set(rows.map((x) => x.Category).filter(Boolean))];
        console.log('🔍 FRONTEND: Extracted categories:', uniqueCategories);
        setCategories(uniqueCategories);
      })
      .catch((err) => {
        console.error('🔍 FRONTEND: Error fetching supply rates:', err);
        setMaterials([]);
        setCategories([]);
      });
  }, [selectedRegion]);

  const filteredMaterials = useMemo(() => {
    const src = Array.isArray(materials) ? materials : [];
    console.log('🔍 FRONTEND: Filtering materials. Source count:', src.length);
    console.log('🔍 FRONTEND: Active filters:', filters);
    
    const filtered = src.filter((m) =>
      (filters.MeasurementUnitRateId === undefined || m.MeasurementUnitRateId?.toString().includes(filters.MeasurementUnitRateId)) &&
      (filters.MeasurementUnitName === undefined || m.MeasurementUnitName?.toLowerCase().includes((filters.MeasurementUnitName || '').toLowerCase())) &&
      (filters.Category === undefined || m.Category === filters.Category) &&
      (filters.SKU === undefined || m.SKU === filters.SKU) &&
      (filters.QuantityTypeName === undefined || m.QuantityTypeName?.toLowerCase().includes((filters.QuantityTypeName || '').toLowerCase()))
    );
    
    console.log('🔍 FRONTEND: Filtered materials count:', filtered.length);
    return filtered;
  }, [materials, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    console.log('🔍 FRONTEND: Filter changed:', name, '=', value);
    setFilters((prev) => ({ ...prev, [name]: value === 'All' ? undefined : value }));
  };

  // Add this debug log to see current state
  console.log('🔍 FRONTEND: Current state summary:', {
    selectedRegion,
    regionName,
    materialsCount: materials.length,
    filteredCount: filteredMaterials.length,
    availableRegionsCount: availableRegions.length
  });

  return (
    <div>
      <div className="page-title-section">
        <h1>Supply Rates</h1>
        <div className="region-indicator-under-title">
          🌏 Current Region: <strong>{regionName}</strong>
        </div>
      </div>

      <div className="supply-rates-container">
        <div className="filter-section">
          <h3>Filters</h3>
          <div className="filter-group">
            <label htmlFor="MeasurementUnitRateId">ID</label>
            <input name="MeasurementUnitRateId" value={filters.MeasurementUnitRateId || ''} onChange={handleFilterChange} placeholder="Filter by ID" />
          </div>
          <div className="filter-group">
            <label htmlFor="MeasurementUnitName">Name</label>
            <input name="MeasurementUnitName" value={filters.MeasurementUnitName || ''} onChange={handleFilterChange} placeholder="Filter by Name" />
          </div>
          <div className="filter-group">
            <label htmlFor="Category">Category</label>
            <select name="Category" value={filters.Category || 'All'} onChange={handleFilterChange}>
              <option value="All">All</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="SKU">SKU</label>
            <input name="SKU" value={filters.SKU || ''} onChange={handleFilterChange} placeholder="Filter by SKU" />
          </div>
          <div className="filter-group">
            <label htmlFor="QuantityTypeName">Quantity Type</label>
            <input name="QuantityTypeName" value={filters.QuantityTypeName || ''} onChange={handleFilterChange} placeholder="Filter by Quantity Type" />
          </div>
        </div>

        <div className="table-section">
          {filteredMaterials.length === 0 ? (
            <p>No supply rates found for {regionName}…</p>
          ) : (
            <table className="supply-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Category</th>
                  <th>Name</th>
                  <th>Quantity Type</th>
                  <th>Value</th>
                  <th>SKU</th>
                  <th>Vendor</th>
                  {/* 🆕 New Date Added column */}
                  <th>Date Added</th>
                </tr>
              </thead>
              <tbody>
                {filteredMaterials.map((r, i) => {
                  const dateStatus = getDateStatus(r.date_added);
                  
                  return (
                    <tr key={`${r.MeasurementUnitRateId}-${i}`}>
                      <td>{r.MeasurementUnitRateId}</td>
                      <td>{r.Category}</td>
                      <td>{r.MeasurementUnitName}</td>
                      <td>{r.QuantityTypeName}</td>
                      <td>{r.Value}</td>
                      <td>{r.SKU || 'N/A'}</td>
                      <td>{r.Vendor || 'N/A'}</td>
                      {/* 🆕 Date cell with color coding */}
                      <td className={`date-cell date-${dateStatus}`}>
                        <span className="date-indicator" title={r.date_added ? `Created: ${formatDate(r.created_at)}\nUpdated: ${formatDate(r.updated_at)}` : 'No breakdown record'}>
                          {formatDate(r.date_added)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupplyRatesPage;
